import React from 'react'

import {membershipApiClient} from '#/lib/api/membership/client'
import {listenSessionResumed} from '#/state/events'
import * as persisted from '#/state/persisted'
import {useSession} from '#/state/session'

type PremiumState = {
  isPremium: boolean
  expiresAt: string | undefined
  saleId: string | undefined
  isTrial: boolean
  hasUsedTrial: boolean
  isLoading: boolean
}

type PremiumApi = {
  setPremiumStatus: (
    isPremium: boolean,
    expiresAt?: string,
    saleId?: string,
  ) => void
  clearPremiumStatus: () => void
  refreshPremiumStatus: () => Promise<void>
}

const StateContext = React.createContext<PremiumState>({
  isPremium: false,
  expiresAt: undefined,
  saleId: undefined,
  isTrial: false,
  hasUsedTrial: false,
  isLoading: false,
})

const ApiContext = React.createContext<PremiumApi>({
  setPremiumStatus: () => {},
  clearPremiumStatus: () => {},
  refreshPremiumStatus: async () => {},
})

export function Provider({children}: React.PropsWithChildren<{}>) {
  const {currentAccount} = useSession()
  const hasHadSession = React.useRef(false)
  const [state, setState] = React.useState<PremiumState>(() => {
    const premium = persisted.get('premium')
    return {
      isPremium: premium?.isPremium ?? false,
      expiresAt: premium?.expiresAt,
      saleId: premium?.saleId,
      isTrial: premium?.isTrial ?? false,
      hasUsedTrial: premium?.hasUsedTrial ?? false,
      isLoading: false,
    }
  })

  // Fetch premium status from server when user logs in
  const fetchPremiumStatus = React.useCallback(async (userDid: string) => {
    setState(prev => ({...prev, isLoading: true}))
    try {
      const response = await membershipApiClient.checkPremium(userDid)
      const newState = {
        isPremium: response.isPremium,
        expiresAt: response.expiresAt ?? undefined,
        saleId: response.saleId ?? undefined,
        isTrial: response.isTrial ?? false,
        hasUsedTrial: response.hasUsedTrial ?? false,
        isLoading: false,
      }
      setState(newState)
      // Sync to local storage
      persisted.write('premium', {
        isPremium: response.isPremium,
        expiresAt: response.expiresAt ?? undefined,
        saleId: response.saleId ?? undefined,
        isTrial: response.isTrial ?? false,
        hasUsedTrial: response.hasUsedTrial ?? false,
      })
    } catch (error) {
      console.error('Failed to fetch premium status:', error)
      // Fall back to local storage on error
      setState(prev => ({...prev, isLoading: false}))
    }
  }, [])

  // Fetch premium status when user changes
  React.useEffect(() => {
    if (currentAccount?.did) {
      hasHadSession.current = true
      fetchPremiumStatus(currentAccount.did)
    } else if (hasHadSession.current) {
      // Genuine logout — clear premium status
      setState({
        isPremium: false,
        expiresAt: undefined,
        saleId: undefined,
        isTrial: false,
        hasUsedTrial: false,
        isLoading: false,
      })
    }
    // Otherwise: session still hydrating — keep cached state from persisted storage
  }, [currentAccount?.did, fetchPremiumStatus])

  React.useEffect(() => {
    return persisted.onUpdate('premium', premium => {
      setState(prev => ({
        ...prev,
        isPremium: premium?.isPremium ?? false,
        expiresAt: premium?.expiresAt,
        saleId: premium?.saleId,
        isTrial: premium?.isTrial ?? false,
        hasUsedTrial: premium?.hasUsedTrial ?? false,
      }))
    })
  }, [])

  // Refresh premium status when session is resumed (user returns to app)
  React.useEffect(() => {
    return listenSessionResumed(() => {
      if (currentAccount?.did) {
        fetchPremiumStatus(currentAccount.did)
      }
    })
  }, [currentAccount?.did, fetchPremiumStatus])

  const api = React.useMemo<PremiumApi>(
    () => ({
      setPremiumStatus: (isPremium, expiresAt, saleId) => {
        const newState = {
          isPremium,
          expiresAt,
          saleId,
          isTrial: false,
          hasUsedTrial: true,
          isLoading: false,
        }
        setState(newState)
        persisted.write('premium', {
          isPremium,
          expiresAt,
          saleId,
          isTrial: false,
          hasUsedTrial: true,
        })
      },
      clearPremiumStatus: () => {
        setState({
          isPremium: false,
          expiresAt: undefined,
          saleId: undefined,
          isTrial: false,
          hasUsedTrial: false,
          isLoading: false,
        })
        persisted.write('premium', undefined)
      },
      refreshPremiumStatus: async () => {
        if (currentAccount?.did) {
          await fetchPremiumStatus(currentAccount.did)
        }
      },
    }),
    [currentAccount?.did, fetchPremiumStatus],
  )

  return (
    <StateContext.Provider value={state}>
      <ApiContext.Provider value={api}>{children}</ApiContext.Provider>
    </StateContext.Provider>
  )
}

export function usePremium() {
  return React.useContext(StateContext)
}

export function usePremiumApi() {
  return React.useContext(ApiContext)
}
